import React, { useState, useEffect } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Menu, X, ChevronDown, Bell } from 'lucide-react';
import { COMPANY, NAV_LINKS } from '../mock';
import { useAuth } from '../marketplace/AuthContext';
import api from '../marketplace/api';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unread, setUnread] = useState(0);
  const [adminUnread, setAdminUnread] = useState(0);
  const location = useLocation();
  const { user, logout } = useAuth();
  const dashPath = user ? (user.role === 'operative' ? '/dashboard/operative' : '/dashboard/client') : '/auth';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let active = true;
    const poll = () => api.get('/my/unread').then((r) => { if (active) setUnread(r.data.count || 0); }).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || !user.isAdmin) { setAdminUnread(0); return; }
    let active = true;
    const poll = () => api.get('/admin/notifications/unread-count').then((r) => { if (active) setAdminUnread(r.data.count || 0); }).catch(() => {});
    poll();
    const t = setInterval(poll, 4000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  useEffect(() => { setOpen(false); setMenu(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-white transition-shadow ${scrolled ? 'shadow-md' : 'border-b border-gray-100'}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <img src={COMPANY.logo} alt="E.A. Lead Solutions" className="h-14 w-auto object-contain" />
          <span className="hidden font-display text-sm font-700 leading-tight text-[#1b4f72] xl:block">
            <span className="block font-bold">E.A. Lead Solutions</span>
          </span>
        </Link>

        <nav className="mx-6 hidden flex-1 items-center justify-center gap-5 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `ea-link-underline whitespace-nowrap text-[15px] font-medium transition-colors ${isActive ? 'text-[#1b4f72]' : 'text-gray-600 hover:text-[#1b4f72]'}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/find-a-professional" className="ea-link-underline whitespace-nowrap text-[15px] font-medium text-gray-600 hover:text-[#1b4f72]">Find a Professional</NavLink>
          {user && user.role === 'operative' && (
            <NavLink to="/browse-jobs" className="ea-link-underline whitespace-nowrap text-[15px] font-medium text-gray-600 hover:text-[#1b4f72]">Browse Jobs</NavLink>
          )}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          {user && user.isAdmin && (
            <Link to="/admin?view=notifications" data-testid="admin-bell" className="relative rounded-full p-2 text-[#1b4f72] hover:bg-gray-100">
              <Bell size={20} />
              {adminUnread > 0 && <span data-testid="admin-bell-badge" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">{adminUnread}</span>}
            </Link>
          )}
          {user ? (
            <div className="relative">
              <button data-testid="user-menu-btn" onClick={() => setMenu((m) => !m)} className="relative flex items-center gap-2 rounded-full border border-gray-200 py-1.5 pl-1.5 pr-3 text-sm font-medium text-[#1b4f72] transition-colors hover:bg-gray-50">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1b4f72] text-sm font-bold text-white">{(user.name || 'A').trim().charAt(0).toUpperCase()}</span>
                <span className="max-w-[120px] truncate">{(user.name || 'Account').split(' ')[0]}</span>
                <ChevronDown size={15} className={`shrink-0 transition-transform ${menu ? 'rotate-180' : ''}`} />
                {unread > 0 && <span data-testid="nav-unread-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#3a9b5c] px-1 text-[11px] font-bold text-white">{unread}</span>}
              </button>
              {menu && (
                <div data-testid="user-menu" className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white py-1.5 shadow-lg">
                  <div className="border-b border-gray-100 px-4 py-2.5">
                    <p className="truncate text-sm font-semibold text-[#1b4f72]" data-testid="user-menu-name">{user.name || 'Account'}</p>
                    <p className="truncate text-xs capitalize text-gray-400">{user.role} account</p>
                  </div>
                  {user.isAdmin && <Link to="/admin" className="block px-4 py-2.5 text-sm font-semibold text-[#3a9b5c] hover:bg-[#e9f3ec]">Admin Dashboard</Link>}
                  <Link to={dashPath} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e9f3ec]">Dashboard</Link>
                  {user.role === 'client' && <Link to="/request-a-service" data-testid="nav-post-job-link" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e9f3ec]">Post a Job</Link>}
                  <Link to="/messages" data-testid="nav-messages-link" className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e9f3ec]">Messages {unread > 0 && <span className="rounded-full bg-[#3a9b5c] px-2 py-0.5 text-xs font-bold text-white">{unread}</span>}</Link>
                  {user.role === 'operative' && <Link to="/profile/operative" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e9f3ec]">My Profile</Link>}
                  <Link to="/change-password" data-testid="nav-change-password" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e9f3ec]">Change Password</Link>
                  <button onClick={logout} data-testid="logout-btn" className="block w-full border-t border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-[#e9f3ec]">Log Out</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" className="whitespace-nowrap text-sm font-medium text-gray-600 hover:text-[#1b4f72]">Log In</Link>
          )}
          {!user && (
            <Link
              to="/request-a-service"
              className="whitespace-nowrap rounded-full bg-[#3a9b5c] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#318150] hover:shadow-md"
            >
              Request a Service
            </Link>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="text-[#1b4f72] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-white lg:hidden">
          <nav className="flex flex-col px-5 py-3">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `border-b border-gray-50 py-3 text-[15px] font-medium ${isActive ? 'text-[#3a9b5c]' : 'text-gray-700'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/find-a-professional" className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Find a Professional</NavLink>
            {user && user.role === 'operative' && <NavLink to="/browse-jobs" className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Browse Jobs</NavLink>}
            {user && user.role === 'operative' && <NavLink to="/profile/operative" className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">My Profile</NavLink>}
            {user && <NavLink to={dashPath} className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Dashboard</NavLink>}
            {user && <NavLink to="/messages" className="flex items-center justify-between border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Messages {unread > 0 && <span className="rounded-full bg-[#3a9b5c] px-2 py-0.5 text-xs font-bold text-white">{unread}</span>}</NavLink>}
            {user && user.isAdmin && <NavLink to="/admin?view=notifications" className="flex items-center justify-between border-b border-gray-50 py-3 text-[15px] font-semibold text-[#3a9b5c]"><span className="flex items-center gap-2"><Bell size={16} /> Notifications</span>{adminUnread > 0 && <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">{adminUnread}</span>}</NavLink>}
            {user && user.isAdmin && <NavLink to="/admin" className="border-b border-gray-50 py-3 text-[15px] font-semibold text-[#3a9b5c]">Admin</NavLink>}
            {user && user.role === 'client' && <NavLink to="/request-a-service" className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Post a Job</NavLink>}
            {user
              ? <button onClick={logout} className="border-b border-gray-50 py-3 text-left text-[15px] font-medium text-gray-700">Log Out</button>
              : <NavLink to="/auth" className="border-b border-gray-50 py-3 text-[15px] font-medium text-gray-700">Log In</NavLink>}
            {!user && (
              <Link
                to="/request-a-service"
                className="mt-3 rounded-full bg-[#3a9b5c] px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Request a Service
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
